import type { ChatMentionsMenuItemType } from '@terra/ui-design-system';
import type {
  EditableFields,
  InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { ObjectContextMarkedField } from '@terra/shared-dto-chat-types';
import { terraProxyApiClient } from '../clients/testerClient';
import {
  useFindingEditStore,
  type PendingAiEdit,
} from '../store/findingEditStore';
import {
  EMPTY_SKILL_DRAFT,
  useSkillDraftsStore,
} from '../store/skillDraftsStore';
import {
  buildFindingContextFields,
  buildFindingContextFieldsFromEditable,
  createFindingObjectContextMention,
  createObjectContextMention,
  createSignalObjectContextMention,
} from './objectContextMentionUtils';
import { buildEditableFieldsFromFinding } from './findingEditableHydration';
import type { TabMentionDragPayload } from './mainContentTabMentionUtils';

function buildEndpointContextFields({
  path,
  url,
  method,
  description,
}: {
  path?: string | null;
  url?: string | null;
  method?: string | null;
  description?: string | null;
}): ObjectContextMarkedField[] {
  const fields: ObjectContextMarkedField[] = [];

  if (method) {
    fields.push({ field: 'method', label: 'Method', value: method });
  }
  if (url) {
    fields.push({ field: 'url', label: 'URL', value: url });
  }
  if (path) {
    fields.push({ field: 'path', label: 'Path', value: path });
  }
  if (description) {
    fields.push({
      field: 'description',
      label: 'Description',
      value: description,
    });
  }

  return fields;
}

function applyEditableFieldsToFinding({
  finding,
  editable,
}: {
  finding: InternalEnrichedFinding;
  editable: EditableFields;
}): InternalEnrichedFinding {
  return {
    ...finding,
    title: editable.title || finding.title,
    shortDescription: editable.shortDescription ?? finding.shortDescription,
    description: editable.description ?? finding.description,
    poc: editable.poc ?? finding.poc,
    businessImpact: editable.businessImpact ?? finding.businessImpact,
    mitigationPlan: editable.mitigationPlan ?? finding.mitigationPlan,
    severity: editable.severity ?? finding.severity,
    status: editable.status ?? finding.status,
  };
}

function mergePendingEditsIntoEditableFields({
  editable,
  pendingEdits,
}: {
  editable: EditableFields;
  pendingEdits?: PendingAiEdit;
}): EditableFields {
  if (!pendingEdits?.fields.length) {
    return editable;
  }

  const merged = { ...editable };
  for (const entry of pendingEdits.fields) {
    Object.assign(merged, { [entry.field]: entry.after });
  }
  return merged;
}

async function resolveFindingTabMention({
  tab,
  targetId,
}: {
  tab: Extract<TabMentionDragPayload['tab'], { type: 'finding' }>;
  targetId: string;
}): Promise<ChatMentionsMenuItemType | null> {
  const findingId = tab.data.findingId;
  const objectLabel = tab.title.replace(/^Edit:\s*/, '');
  const store = useFindingEditStore.getState();
  const editState = store.getFindingEditState(findingId);
  const pendingEdits = store.getPendingAiEdits(findingId);
  const hasPendingEdits = Boolean(pendingEdits?.fields.length);
  const preferEditableState =
    tab.data.mode === 'edit' ||
    Boolean(editState?.hasUnsavedChanges) ||
    editState?.mode === 'edit' ||
    hasPendingEdits;

  if (preferEditableState && editState) {
    const editableFields = mergePendingEditsIntoEditableFields({
      editable: editState.editableFields,
      pendingEdits,
    });
    const fields = buildFindingContextFieldsFromEditable({
      editable: editableFields,
      fallbackTitle: objectLabel,
    });
    if (fields.length > 0) {
      return createObjectContextMention({
        objectKind: 'Finding',
        objectId: findingId,
        objectLabel: editableFields.title || objectLabel,
        fields,
      });
    }
  }

  try {
    const response = await terraProxyApiClient.getFindingById({
      findingId,
      targetId,
    });
    if (response.status !== 200) {
      return null;
    }

    let finding = response.body as InternalEnrichedFinding;
    if (hasPendingEdits) {
      const baseEditable =
        editState?.editableFields ??
        buildEditableFieldsFromFinding({ finding });
      finding = applyEditableFieldsToFinding({
        finding,
        editable: mergePendingEditsIntoEditableFields({
          editable: baseEditable,
          pendingEdits,
        }),
      });
    } else if (
      editState &&
      (editState.hasUnsavedChanges || editState.mode === 'edit')
    ) {
      finding = applyEditableFieldsToFinding({
        finding,
        editable: editState.editableFields,
      });
    }

    return createFindingObjectContextMention(finding, {
      fields: buildFindingContextFields(finding),
    });
  } catch (error: unknown) {
    console.warn(
      'Failed to resolve finding tab mention; falling back to edit-state context',
      error instanceof Error ? error : new Error(String(error)),
    );
    if (editState) {
      const editableFields = mergePendingEditsIntoEditableFields({
        editable: editState.editableFields,
        pendingEdits,
      });
      const fields = buildFindingContextFieldsFromEditable({
        editable: editableFields,
        fallbackTitle: objectLabel,
      });
      if (fields.length > 0) {
        return createObjectContextMention({
          objectKind: 'Finding',
          objectId: findingId,
          objectLabel: editableFields.title || objectLabel,
          fields,
        });
      }
    }
    return null;
  }
}

async function resolveSignalTabMention({
  tab,
}: {
  tab: Extract<TabMentionDragPayload['tab'], { type: 'signal' }>;
}): Promise<ChatMentionsMenuItemType | null> {
  const signalId = tab.data.signalId;
  try {
    const signal = await terraProxyApiClient.getSignal(signalId);
    return createSignalObjectContextMention(signal);
  } catch (error: unknown) {
    console.warn(
      'Failed to load signal for tab mention; using fallback label',
      error instanceof Error ? error : new Error(String(error)),
    );
    return createObjectContextMention({
      objectKind: 'Signal',
      objectId: signalId,
      objectLabel: tab.title,
    });
  }
}

async function resolveEndpointTabMention({
  tab,
}: {
  tab: Extract<TabMentionDragPayload['tab'], { type: 'endpoint' }>;
}): Promise<ChatMentionsMenuItemType | null> {
  const endpointId = tab.data.endpointId;
  const fallbackLabel = tab.data.endpointUrl ?? tab.title;

  try {
    const endpoint = await terraProxyApiClient.getEndpointById(endpointId);
    const fields = buildEndpointContextFields({
      path: endpoint.path,
      url: endpoint.url,
      method: endpoint.request?.method,
      description: endpoint.description,
    });
    return createObjectContextMention({
      objectKind: 'EndPoint',
      objectId: endpointId,
      objectLabel: endpoint.url ?? fallbackLabel,
      fields,
    });
  } catch (error: unknown) {
    console.warn(
      'Failed to load endpoint for tab mention; using fallback label',
      error instanceof Error ? error : new Error(String(error)),
    );
    return createObjectContextMention({
      objectKind: 'EndPoint',
      objectId: endpointId,
      objectLabel: fallbackLabel,
    });
  }
}

function resolveSkillEditorTabMention({
  tab,
}: {
  tab: Extract<TabMentionDragPayload['tab'], { type: 'skill-editor' }>;
}): ChatMentionsMenuItemType | null {
  const skillDraftId = tab.data.skillDraftId;
  const draft =
    useSkillDraftsStore.getState().getDraft(skillDraftId) ?? EMPTY_SKILL_DRAFT;
  const fields: ObjectContextMarkedField[] = [
    { field: 'name', label: 'Name', value: draft.name },
    { field: 'slug', label: 'Slug', value: draft.slug },
    { field: 'description', label: 'Description', value: draft.description },
    { field: 'body', label: 'Body', value: draft.body },
  ].filter((field) => field.value.trim().length > 0);

  return createObjectContextMention({
    objectKind: 'Skill',
    objectId: tab.data.skillId ?? skillDraftId,
    objectLabel: tab.title,
    fields,
  });
}

export async function resolveTabMentionWithContent({
  payload,
  targetId,
}: {
  payload: TabMentionDragPayload;
  targetId: string | null | undefined;
}): Promise<ChatMentionsMenuItemType | null> {
  const { tab } = payload;

  switch (tab.type) {
    case 'finding':
      if (!targetId) {
        return null;
      }
      return resolveFindingTabMention({ tab, targetId });
    case 'signal':
      return resolveSignalTabMention({ tab });
    case 'endpoint':
      return resolveEndpointTabMention({ tab });
    case 'skill-editor':
      return resolveSkillEditorTabMention({ tab });
    case 'webpage':
      return createObjectContextMention({
        objectKind: 'WebPage',
        objectId: tab.data.id,
        objectLabel: tab.title,
      });
    case 'executions':
      return createObjectContextMention({
        objectKind: 'Trace',
        objectId: tab.data.threadId,
        objectLabel: tab.title,
      });
    case 'skill':
      return createObjectContextMention({
        objectKind: 'Skill',
        objectId: tab.data.skillId,
        objectLabel: tab.title,
      });
    default:
      return null;
  }
}
